import { useCallback, useEffect, useState } from "react";
import { api, type MyProfile } from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { Dialog } from "../components/Dialog";
import {
  Button,
  Failed,
  Field,
  Label,
  SkeletonRows,
  TextArea,
} from "../components/primitives";
import { useSession, roleLabel } from "../lib/session";

/**
 * Your own entry, opened from the identity box in the rail.
 *
 * **Why it is a dialog and not a screen.** It used to live at `/bench`, which
 * meant a consultant's profile and the firm's directory were the same route
 * wearing two labels — and for a Lead or a Partner there was no way to reach
 * their own entry at all. A profile is a thing you glance at and amend, from
 * wherever you happen to be; it is not a destination. The identity box is
 * where everybody already looks for it.
 *
 * **Everyone firm-side gets one.** `consultant_profile` has never had a role
 * constraint and `consultant_profile_upsert` has always read
 * `user_id = app.uid() OR app.is_super_admin()`, so the schema always expected
 * this. What was missing was a door. Since 051 the bench searches across every
 * firm-side entry, so a Partner writing "runs the water practice" is findable
 * by it — which is the point of letting them write one.
 *
 * **It persists in Postgres, not in the browser.** The request said "save to
 * storage so users can see details anytime they click their profile button",
 * and the only reading of that which also satisfies "searchable from bench" is
 * server-side: `localStorage` would be invisible to every other person in the
 * firm and would vanish on a new device. `PUT /api/me/profile` is the store.
 */
export function ProfileDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Nothing exists until it is opened, which is the whole fix for a bug the
  // user reported on 2026-10-28: a saved headline was not there on reopening.
  //
  // This component used to stay mounted and pass `open` down into the fetch,
  // resolving `null` while shut. That left the resource parked at `{ state:
  // "ready", data: null }` -- indistinguishable, to the effect that seeds the
  // fields, from a real empty profile. On the click that opened it, effects
  // ran in declaration order: `useResource` set itself back to loading and
  // began the real fetch, and the seeding effect *below* it still read the
  // previous render's stale `ready/null`, blanked all four fields and marked
  // itself done. The real profile landed a moment later and was discarded.
  //
  // Mounting on demand removes the state the bug needed. Every opening starts
  // at `loading`, so the only `ready` that can ever be seeded from is an
  // answer from the server. Refetch-on-reopen -- somebody else may have
  // amended your entry since you last looked -- comes free with a fresh mount.
  if (!open) return null;
  return <ProfileForm onClose={onClose} />;
}

function ProfileForm({ onClose }: { onClose: () => void }) {
  const { me } = useSession();

  const profile = useResource<MyProfile | null>(
    () => api.get<MyProfile | null>("/api/me/profile"),
    [],
  );

  const [headline, setHeadline] = useState("");
  const [expertise, setExpertise] = useState("");
  const [bio, setBio] = useState("");
  const [availability, setAvailability] = useState("");
  const [saved, setSaved] = useState(false);
  /** Set once the server's copy has been loaded into the fields. */
  const [loaded, setLoaded] = useState(false);

  // Seed the fields from the server exactly once. Not with useState's
  // initialiser, because the resource is still loading when this first
  // renders and the initialiser would capture empty strings.
  useEffect(() => {
    if (loaded || profile.resource.state !== "ready") return;
    const p = profile.resource.data;
    setHeadline(p?.headline ?? "");
    setExpertise((p?.expertise ?? []).join(", "));
    setBio(p?.bio ?? "");
    setAvailability(p?.availability ?? "");
    setLoaded(true);
  }, [loaded, profile.resource]);

  const { invoke, pending, refusal } = useAction(async () => {
    // PUT, and every field goes each time: the route replaces the entry
    // rather than amending it, so a field left out would be a field cleared.
    await api.put("/api/me/profile", {
      headline: headline.trim(),
      expertise: expertise
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean),
      bio: bio.trim(),
      availability: availability.trim(),
    });
    setSaved(true);
  });

  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void invoke();
    },
    [invoke],
  );

  if (!me) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Your profile"
      description="What the firm sees when it is deciding who to put on a piece of work. Your name and role come from your account; everything below is yours to write."
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            form="profile-form"
            tone="primary"
            loading={pending}
          >
            Save
          </Button>
          <Button type="button" onClick={onClose}>
            Close
          </Button>
          {/* Shown rather than reloading the page behind: the dialog is the
              thing holding what was just saved, and a parent reload would
              unmount it mid-sentence. People.tsx learned this on 2026-10-27. */}
          {saved && !pending && (
            <span className="text-xs text-muted" role="status">
              Saved. The firm can find you by this.
            </span>
          )}
        </div>
      }
    >
      {/* Identity, which is not editable here. Changing your own name or role
          is an account matter and belongs to a Super Admin — showing it as a
          field would invite an edit the server would refuse. */}
      <div className="border-b border-line pb-5">
        <Label as="h3">Account</Label>
        <p className="mt-2 text-base text-ink">{me.fullName}</p>
        <p className="mt-0.5 text-xs text-muted">{roleLabel(me.role)}</p>
      </div>

      {profile.resource.state === "loading" && (
        <div className="mt-5">
          <SkeletonRows rows={4} />
        </div>
      )}

      {profile.resource.state === "failed" && (
        <Failed
          message={profile.resource.message}
          correlationId={profile.resource.correlationId}
        />
      )}

      {/* Two columns from `sm`, and shorter hints than the first draft
          carried. Four stacked fields each explained in a sentence and
          a half came to about 545px of body before the header and
          footer were counted, which is why this scrolled on an
          ordinary laptop. The hints still say the thing that is not
          obvious — what the field is FOR — and stop saying the part a
          person can see for themselves. */}
      {loaded && (
        <form
          id="profile-form"
          onSubmit={submit}
          className="mt-5 grid gap-x-5 gap-y-4 sm:grid-cols-2"
        >
          <Field
            label="Headline"
            hint="Read before you walk into a client's room."
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            maxLength={200}
            autoComplete="off"
            data-autofocus="true"
          />
          <Field
            label="Availability"
            hint="Your words — “two days a week”, “free from March”."
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
            maxLength={200}
            autoComplete="off"
          />
          <div className="sm:col-span-2">
            <Field
              label="Expertise"
              hint="Comma separated. These are the tags the bench filters on, so plain words beat clever ones."
              value={expertise}
              onChange={(e) => setExpertise(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="sm:col-span-2">
            <TextArea
              label="Background"
              hint="Searched whole, so write it for a person rather than for a keyword."
              rows={3}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={4000}
            />
          </div>

          {refusal && (
            <div className="sm:col-span-2">
              <Failed
                message={refusal.message}
                correlationId={refusal.correlationId}
              />
            </div>
          )}
        </form>
      )}
    </Dialog>
  );
}
